// THE LIVE DECK — Presentation mode's answer as a VIEW of the main area
// (AI_ASSISTANT_SPEC v1.1 §12.1–§12.2; the ai.boards DECK contract v1). Nika
// 2026-09-29: Presentation = "Live deck in thread".
//
// A title slide, then the content slides — ONE slide on the stage at a time,
// Previous / Next under it (and ← / → while the view shows), a counter, and
// three verbs in the head: Present (the full-viewport presenter,
// AssistantDeckPresenter below), Save (the saved boards row, kind 'deck') and
// the ONE square close. A content slide is a headline (+ subtitle) and one or
// two LIVE cards — the board's own card (AssistantBoardCard, reused through
// AssistantDeckCard), so a slide mounts an installed app's widget with its
// params or a records block whose rows come from the SDK, never a number the
// model wrote — and the speaker notes under it.
//
// It lays over the main area like the generated board: on the home over the
// grid area (pages/main.js), on every other route in the page layer
// (components/AssistantMainLayer.js). It gates and crossfades itself (Rule 66:
// closed keeps the box, only opacity and visibility change); the slide on the
// stage fades in when it changes. States: building (a skeleton slide, the
// slides join as they stream), ready, failed (one sentence + Try again), not
// available here (an older SDK or server — one sentence, no call made), an
// empty slide (its items cannot be mounted here — one sentence).
//
// Every value comes from functions/assistantDeck.js through `el.call`.
export const AssistantDeckView = {
  tag: 'section',
  role: 'region',
  ariaLabel: (el) => el.call('polyglot', 'workspace.assistantDeck.region'),
  ariaRoledescription: (el) => el.call('polyglot', 'workspace.assistantDeck.roleDescription'),

  dataOpen: (el, s) => (el.call('assistantDeckShown', s.root) ? 'true' : 'false'),
  dataStatus: (el, s) => {
    const vm = el.call('assistantDeckViewModel', s.root)
    return (vm && vm.status) || 'idle'
  },
  ariaHidden: (el, s) => (el.call('assistantDeckShown', s.root) ? null : 'true'),
  ariaBusy: (el, s) => {
    const vm = el.call('assistantDeckViewModel', s.root)
    return vm && (vm.status === 'loading' || vm.status === 'streaming') ? 'true' : null
  },

  display: 'flex',
  flexDirection: 'column',
  gap: 'B',
  // ONE column with the board: the home grid's own width on the home, the
  // layer's width over a page.
  width: '100%',
  maxWidth: 'var(--home-grid-w, 100%)',
  marginInline: 'auto',
  minWidth: '0',
  boxSizing: 'border-box',
  // brought to the top as a new deck (below), it stays clear of the shell's
  // floating header — the Fly page's own margin
  scrollMarginTop: 'calc(var(--shell-top) + var(--spacing-A))',
  // The crossfade (the board's recipe): `C`, opacity only.
  opacity: '0',
  visibility: 'hidden',
  pointerEvents: 'none',
  transition: 'opacity C defaultBezier, visibility 0s linear C',
  '&[data-open="true"]': {
    opacity: '1',
    visibility: 'visible',
    pointerEvents: 'auto',
    transition: 'opacity C defaultBezier, visibility 0s linear 0s'
  },

  // Esc and ← / → join the shell keyboard registry while the view is on the
  // page; the installed apps publishing re-derive the slides.
  subscribeTo: ['workspace', 'workspaceApps', 'workspaceProject'],
  onMounted: (el) => el.call('assistantDeckShortcuts', el),
  onUpdate: (el, s) => el.call('assistantDeckRederive', s && s.root),
  // A NEW DECK — an ask, a refine, a saved deck opened — comes to the top of
  // the view, once per request (Nika 2026-10-23: "when answering in new
  // section always scroll top of that section"; functions/assistantReveal.js).
  // A view shorter than the scroller takes the room it needs (its
  // `minHeight`), while it is on screen.
  minHeight: (el, s) =>
    el.call('assistantRevealRoom', el, s.root, el.call('assistantDeckShown', s.root)),
  stateDeps: [
    (el, s) => {
      const d = s.root && s.root._aiDeck
      return d && (d.requestId || d.id) ? String(d.requestId || d.id) : null
    }
  ],
  onStateUpdate: (el, s, ctx, change) => {
    const next = ((change && change.next) || [])[0]
    if (next && el.call('assistantDeckShown', s.root)) {
      el.call('assistantRevealTop', el, null, 'deck' + next)
    }
  },
  onRemove: (el) => {
    if (typeof el.__deckShortcutsOff === 'function') el.__deckShortcutsOff()
  },

  DeckHead: {
    flow: 'y',
    gap: 'Z',
    minWidth: '0',

    // The greeting line's type, like the board's title; a reading position
    // after the deck lands (`tabindex -1`, no ring).
    DeckTitle: {
      tag: 'h2',
      dataDeckTitle: true,
      tabindex: '-1',
      margin: '0',
      fontSize: 'C',
      fontWeight: '700',
      lineHeight: '1.2',
      color: 'title',
      minWidth: '0',
      overflowWrap: 'anywhere',
      ':focus': { outline: 'none' },
      ':focus-visible': { outline: 'none', boxShadow: 'none' },
      TitleText: {
        tag: 'span',
        text: (el, s) => {
          const vm = el.call('assistantDeckViewModel', s.root)
          return (vm && vm.title) || ''
        }
      },
      Skeleton: {
        display: 'inline-block',
        verticalAlign: 'middle',
        width: 'F',
        height: '1em',
        hide: (el, s) => {
          const vm = el.call('assistantDeckViewModel', s.root)
          return !vm || !!vm.title || vm.status === 'failed'
        }
      }
    },

    DeckBar: {
      flow: 'x',
      flexWrap: 'wrap',
      align: 'center space-between',
      gap: 'Z',
      minWidth: '0',

      AppMicroLabel: {
        dataDeckMicro: true,
        whiteSpace: 'normal',
        text: (el, s) => el.call('assistantDeckMicro', el.call('assistantDeckViewModel', s.root))
      },

      DeckActions: {
        flow: 'x',
        flexWrap: 'wrap',
        align: 'center',
        gap: 'Y',

        AppCapsuleBtnPrimary: {
          compact: true,
          dataDeckPresent: true,
          disabled: (el, s) => {
            const vm = el.call('assistantDeckViewModel', s.root)
            return !vm || !(vm.slides || []).length || vm.status === 'loading' ? true : null
          },
          text: '{{ workspace.assistantDeck.present | polyglot }}',
          onClick: (e, el) => el.call('assistantDeckPresent')
        },

        AppCapsuleBtnToolbar: {
          compact: true,
          dataDeckSave: true,
          disabled: (el, s) =>
            el.call('assistantDeckActionsIdle', el.call('assistantDeckViewModel', s.root)) || null,
          onClick: (e, el, s) => el.call('saveDeck', el, s),
          AppLabelStack: {
            labels: [
              'workspace.assistantDeck.save',
              'workspace.assistantDeck.saving',
              'workspace.assistantDeck.saved'
            ],
            shown: (el, s) =>
              el.call('assistantDeckSaveLabel', el.call('assistantDeckViewModel', s.root))
          }
        },

        // The ONE square button of the head, named by this view's verb (only
        // the brand's own `attr` key replaces the brand default).
        CloseButton: {
          dataDeckDismiss: true,
          attr: { 'aria-label': (el) => el.call('polyglot', 'workspace.assistantDeck.dismiss') },
          onClick: (e, el) => el.call('assistantDeckDismiss', { focusComposer: true })
        }
      }
    },

    // A failure (with Try again) or a save notice. Always mounted — a status
    // region must exist before it speaks.
    DeckStatus: {
      dataDeckStatus: true,
      role: 'status',
      flow: 'x',
      flexWrap: 'wrap',
      align: 'center',
      gap: 'Z',
      minWidth: '0',
      StatusText: {
        tag: 'span',
        fontSize: 'A',
        fontWeight: '500',
        color: 'title',
        minWidth: '0',
        overflowWrap: 'anywhere',
        text: (el, s) => el.call('assistantDeckStatus', el.call('assistantDeckViewModel', s.root))
      },
      AppCapsuleBtnToolbar: {
        compact: true,
        dataDeckRetry: true,
        hide: (el, s) => {
          const vm = el.call('assistantDeckViewModel', s.root)
          return !vm || vm.status !== 'failed' || vm.error === 'unavailable'
        },
        text: '{{ workspace.assistantBoard.tryAgain | polyglot }}',
        onClick: (e, el) => el.call('assistantDeckRetry')
      }
    }
  },

  // The stage: the ONE slide on screen, keyed by the slide — a new slide is a
  // new element, so it fades in; the stage keeps its height floor.
  DeckStage: {
    dataDeckStage: true,
    ariaLive: 'polite',
    minWidth: '0',
    children: (el, s) => {
      const slide = el.call('assistantDeckSlide', el.call('assistantDeckViewModel', s.root))
      return slide ? [slide] : []
    },
    childrenAs: 'state',
    childExtends: 'AssistantDeckSlide'
  },

  // Previous · 3 / 6 · Next — under the stage, centred.
  DeckNav: {
    dataDeckNav: true,
    flow: 'x',
    align: 'center center',
    gap: 'Z',
    minWidth: '0',
    hide: (el, s) => {
      const vm = el.call('assistantDeckViewModel', s.root)
      return !vm || (vm.slides || []).length < 2
    },
    AppCapsuleBtnToolbar_prev: {
      compact: true,
      dataDeckPrev: true,
      disabled: (el, s) =>
        el.call('assistantDeckCanGo', el.call('assistantDeckViewModel', s.root), -1) ? null : true,
      text: '{{ workspace.assistantDeck.previous | polyglot }}',
      onClick: (e, el) => el.call('assistantDeckGo', -1)
    },
    DeckCounter: {
      tag: 'span',
      dataDeckCounter: true,
      fontSize: 'Z',
      fontWeight: '500',
      color: 'caption',
      fontVariantNumeric: 'tabular-nums',
      text: (el, s) => el.call('assistantDeckCounter', el.call('assistantDeckViewModel', s.root))
    },
    AppCapsuleBtnToolbar_next: {
      compact: true,
      dataDeckNext: true,
      disabled: (el, s) =>
        el.call('assistantDeckCanGo', el.call('assistantDeckViewModel', s.root), 1) ? null : true,
      text: '{{ workspace.assistantDeck.next | polyglot }}',
      onClick: (e, el) => el.call('assistantDeckGo', 1)
    }
  }
}

// ONE slide (the stage's child, the presenter's stage): the title slide — the
// deck's title large, the subtitle under it — or a content slide — the
// headline, the subtitle, one or two live cards, the speaker notes. While the
// deck builds, a skeleton slide at the same height. Guards `s` — a
// childExtends template evaluates once unbound.
export const AssistantDeckSlide = {
  tag: 'article',
  dataSlide: (el, s) => (s && s.key) || null,
  dataKind: (el, s) => (s && s.kind) || null,
  ariaBusy: (el, s) => (s && s.kind === 'skeleton' ? 'true' : null),
  flow: 'y',
  gap: 'B',
  minWidth: '0',
  minHeight: 'H',
  boxSizing: 'border-box',
  padding: 'C',
  round: 'B1',
  background: 'var(--home-card-bg, var(--theme-card-background))',
  color: 'title',
  animation: 'fadeIn',
  animationDuration: 'C',
  animationTimingFunction: 'defaultBezier',
  animationFillMode: 'backwards',
  '@mobileL': { padding: 'B', minHeight: 'G' },

  // The title slide: the deck's title as the page's largest type, centred.
  '.isTitleSlide': { justifyContent: 'center', alignItems: 'center', textAlign: 'center' },
  isTitleSlide: (el, s) => !!(s && s.kind === 'title'),

  SlideTitle: {
    tag: 'h3',
    dataSlideTitle: true,
    hide: (el, s) => !s || s.kind === 'skeleton',
    margin: '0',
    fontSize: (el, s) => (s && s.kind === 'title' ? 'D' : 'C'),
    fontWeight: '700',
    lineHeight: '1.2',
    color: 'title',
    minWidth: '0',
    overflowWrap: 'anywhere',
    text: (el, s) => (s && s.title) || ''
  },
  SlideSubtitle: {
    tag: 'p',
    hide: (el, s) => !s || !s.subtitle,
    margin: '0',
    fontSize: 'A',
    color: 'caption',
    minWidth: '0',
    overflowWrap: 'anywhere',
    text: (el, s) => (s && s.subtitle) || ''
  },

  // One or two live cards: the board's own card (AssistantDeckCard) — a
  // widget with its params, or a records block whose rows the SDK brings.
  SlideItems: {
    dataSlideItems: true,
    hide: (el, s) => !s || s.kind !== 'content' || !(s.items || []).length,
    display: 'flex',
    flexWrap: 'wrap',
    gap: 'A',
    minWidth: '0',
    children: (el, s) => (s && Array.isArray(s.items) ? s.items : []),
    childrenAs: 'state',
    childExtends: 'AssistantDeckCard'
  },

  // A content slide whose items cannot be mounted here: one sentence, never
  // a made-up value.
  SlideEmpty: {
    tag: 'p',
    dataSlideEmpty: true,
    hide: (el, s) => !s || s.kind !== 'content' || (s.items || []).length > 0,
    margin: '0',
    fontSize: 'A',
    color: 'caption',
    text: '{{ workspace.assistantDeck.emptySlide | polyglot }}'
  },

  // While the deck builds: a headline bar and a card-sized block.
  SlideSkeleton: {
    hide: (el, s) => !s || s.kind !== 'skeleton',
    flow: 'y',
    gap: 'A',
    flex: '1',
    minHeight: '0',
    Skeleton_1: { width: '50%', height: 'C' },
    Skeleton_2: { flex: '1', minHeight: 'F', round: 'radiusControl' }
  },

  // The speaker notes: qualitative lines for the presenter.
  SlideNotes: {
    tag: 'aside',
    dataSlideNotes: true,
    hide: (el, s) => !s || !s.notes,
    marginTop: 'auto',
    flow: 'y',
    gap: 'X',
    minWidth: '0',
    AppMicroLabel: { text: '{{ workspace.assistantDeck.notes | polyglot }}' },
    NotesText: {
      tag: 'p',
      margin: '0',
      fontSize: 'Z',
      color: 'caption',
      overflowWrap: 'anywhere',
      text: (el, s) => (s && s.notes) || ''
    }
  }
}

// A slide's card: the generated board's card, with no Pin (a slide item is
// not pinnable from a deck) and a floor that gives a chart or a list room.
export const AssistantDeckCard = {
  extends: 'AssistantBoardCard',
  flex: '1 1 0',
  minWidth: 'G',
  minHeight: 'G',
  '@mobileL': { minWidth: '100%' },
  CardHead: {
    HomeWidgetAction: { hide: () => true }
  }
}

// THE PRESENTER — the deck on the full viewport, in the shell's ModalRoot
// stack (functions/assistantDeck.js assistantDeckPresent): the slide on
// screen at presentation size, and a quiet bar with Previous · the counter ·
// Next · Exit. Esc and a press on the backdrop close it (the ModalRoot's own
// layer — one Escape, one layer); ← / → / PageUp / PageDown / Space / Home /
// End move on its own focused surface (the shell registry stands down under a
// modal).
export const AssistantDeckPresenter = {
  tabindex: '0',
  role: 'group',
  ariaLabel: (el) => el.call('polyglot', 'workspace.assistantDeck.presenterLabel'),
  ariaRoledescription: (el) => el.call('polyglot', 'workspace.assistantDeck.roleDescription'),
  flow: 'y',
  gap: 'B',
  width: '100%',
  height: 'calc(100dvh - 2 * var(--spacing-A))',
  minWidth: '0',
  boxSizing: 'border-box',
  padding: 'C',
  round: 'radiusSheet',
  background: 'gray2',
  '@light': { background: 'gray16' },
  outline: 'none',
  ':focus-visible': { outline: 'none' },
  '@mobileL': { padding: 'A' },
  onRender: (el) => {
    const node = el.node
    if (
      node &&
      typeof node.focus === 'function' &&
      node.ownerDocument &&
      node.ownerDocument.activeElement !== node
    ) {
      node.focus({ preventScroll: true })
    }
  },
  onKeydown: (e, el) => el.call('assistantDeckPresenterKey', e),

  PresenterStage: {
    flex: '1',
    minHeight: '0',
    overflow: 'auto',
    display: 'flex',
    flexDirection: 'column',
    children: (el, s) => {
      const slide = el.call('assistantDeckSlide', el.call('assistantDeckViewModel', s.root))
      return slide ? [slide] : []
    },
    childrenAs: 'state',
    childExtends: 'AssistantDeckSlide',
    childProps: { flex: '1', fontSize: 'B' }
  },

  PresenterBar: {
    flow: 'x',
    align: 'center center',
    gap: 'Z',
    minWidth: '0',
    AppCapsuleBtnToolbar_prev: {
      compact: true,
      disabled: (el, s) =>
        el.call('assistantDeckCanGo', el.call('assistantDeckViewModel', s.root), -1) ? null : true,
      text: '{{ workspace.assistantDeck.previous | polyglot }}',
      onClick: (e, el) => el.call('assistantDeckGo', -1)
    },
    PresenterCounter: {
      tag: 'span',
      fontSize: 'Z',
      fontWeight: '500',
      color: 'caption',
      fontVariantNumeric: 'tabular-nums',
      text: (el, s) => el.call('assistantDeckCounter', el.call('assistantDeckViewModel', s.root))
    },
    AppCapsuleBtnToolbar_next: {
      compact: true,
      disabled: (el, s) =>
        el.call('assistantDeckCanGo', el.call('assistantDeckViewModel', s.root), 1) ? null : true,
      text: '{{ workspace.assistantDeck.next | polyglot }}',
      onClick: (e, el) => el.call('assistantDeckGo', 1)
    },
    AppCapsuleBtnToolbar_exit: {
      compact: true,
      text: '{{ workspace.assistantDeck.exitPresent | polyglot }}',
      onClick: (e, el) => el.call('assistantDeckPresentExit')
    }
  }
}
